import CloseIcon from "@assets/icons/close.svg?react";
import ThinkChinaLogo from "@assets/logo@2x.svg?react";
import ThinkChinaLogoOnDark from "@assets/logo-on-dark.png";
import InputBox from "@components/InputBox/InputBox";
import Link from "@components/Link";
import SocialIcons from "@components/SocialIcons";
import { ThemeEnum } from "@components/ThemeProvider/ThemeProvider";
import { useCustomParams } from "@src/app/hooks/useCustomParams";
import useGTMDispatch from "@src/app/hooks/useGTMDispatch";
import clsx from "clsx";
import { useState } from "react";
import { useLocation } from "react-router";

import {
  categoryDropdownItems,
  categoryNavLinks,
  isCategoryNavItemActive,
} from "./categoryData";
import TriangleIcon from "./TriangleIcon";

type MobileMenuProps = {
  isOpen: boolean;
  onClose: () => void;
  theme?: ThemeEnum;
};

const mainNavLinks = [
  { label: "Events", href: "/events" },
  { label: "Newsletter", href: "/newsletter" },
  { label: "About Us", href: "/about-us" },
  { label: "Contributors", href: "/contributors" },
];

export default function MobileMenu({
  isOpen,
  onClose,
  theme = ThemeEnum.light,
}: MobileMenuProps) {
  const location = useLocation();
  const sendDataToGTM = useGTMDispatch();
  const [expandedCategory, setExpandedCategory] = useState<string | null>(
    "Culture",
  );
  const [searchValue, setSearchValue] = useState("");
  const isDefault = theme === ThemeEnum.light;
  const { level1, level2 } = useCustomParams();
  const pageUrl =
    typeof window !== "undefined"
      ? window.location.href
      : `https://www.thinkchina.sg${location.pathname}${location.search}`;

  if (!isOpen) return null;

  const toggleCategory = (categoryLabel: string) => {
    setExpandedCategory(
      expandedCategory === categoryLabel ? null : categoryLabel,
    );
  };

  return (
    <div
      data-testid="mobile-menu"
      className={clsx(
        "fixed inset-0 z-50",
        isDefault ? "bg-background-default" : "bg-background-primary-on-dark",
      )}
    >
      <div className="no-scrollbar container mx-auto flex h-screen w-full max-w-[1200px] flex-col overflow-y-auto">
        {/* Header Section */}
        <div
          className={clsx(
            "relative flex h-[56px] w-full shrink-0 flex-col items-start",
            isDefault && "bg-background-default",
            theme === ThemeEnum.dark && "bg-background-primary-on-dark",
          )}
        >
          <div className="flex w-full items-center justify-between px-3 py-2">
            {/* Logo */}
            <div className="flex flex-1 items-center">
              <Link to="/" className="h-[40px] w-[134px] shrink-0">
                {isDefault && <ThinkChinaLogo className="size-full" />}
                {theme === ThemeEnum.dark && (
                  <img
                    src={ThinkChinaLogoOnDark}
                    alt="ThinkChina Logo"
                    className="size-full"
                  />
                )}
              </Link>
            </div>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="hover:bg-button-ghost-hover relative size-8 shrink-0 overflow-hidden rounded-[100px] transition-colors"
              aria-label="Close menu"
            >
              <div className="absolute inset-[12.5%] flex items-center justify-center">
                <CloseIcon
                  className={clsx(
                    "size-5",
                    isDefault ? "text-primary" : "text-primary-on-dark",
                  )}
                />
              </div>
            </button>
          </div>
        </div>

        {/* Search Section */}
        <div className={clsx("border-divider-secondary border-b px-3 py-4")}>
          <InputBox
            variant="search"
            placeholder="Search"
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
            }}
            onSubmit={(value) => {
              const q = value.trim();
              if (!q) return;
              onClose();
              window.location.assign(`/search?q=${encodeURIComponent(q)}`);
            }}
          />
        </div>

        {/* Category Navigation */}
        <div
          className={clsx(
            "border-divider-secondary flex flex-col items-start gap-2 border-b px-0 py-4",
          )}
        >
          {categoryNavLinks.map((category) => {
            const isExpanded = expandedCategory === category.label;
            const isActive = isCategoryNavItemActive(
              location.pathname,
              level1,
              level2,
              category,
            );

            if (!category.hasDropdown) {
              return (
                <div key={category.label} className="w-full">
                  <Link
                    to={`${category.href}?ref=mobile-menu`}
                    className={clsx(
                      "flex h-10 w-full items-center px-3 py-0",
                      isActive && "bg-background-secondary",
                    )}
                    onClick={() => {
                      sendDataToGTM({
                        event: "tcCustomEvent",
                        eventCategory: "mobile navigation",
                        eventAction: "click",
                        eventLabel: category.label.toLowerCase(),
                      });
                      onClose();
                    }}
                  >
                    <div className="flex items-center gap-2">
                      {isActive && (
                        <div className="bg-brand-primary size-2 shrink-0 rounded-full" />
                      )}
                      <p
                        className={clsx(
                          "text-button-1 shrink-0",
                          isActive
                            ? "text-brand-primary"
                            : isDefault
                              ? "text-primary"
                              : "text-primary-on-dark",
                        )}
                      >
                        {category.label}
                      </p>
                    </div>
                  </Link>
                </div>
              );
            }

            return (
              <div key={category.label} className="w-full">
                <button
                  type="button"
                  onClick={() => {
                    sendDataToGTM({
                      event: "tcCustomEvent",
                      eventCategory: "mobile navigation",
                      eventAction: "click",
                      eventLabel: category.label.toLowerCase(),
                    });
                    toggleCategory(category.label);
                  }}
                  className={clsx(
                    "flex h-10 w-full items-center justify-between px-3 py-0",
                    isExpanded && "bg-background-secondary",
                  )}
                >
                  <div className="flex items-center gap-2">
                    {isActive && (
                      <div className="bg-brand-primary size-2 shrink-0 rounded-full" />
                    )}
                    <p
                      className={clsx(
                        "text-button-1 shrink-0",
                        isActive
                          ? "text-brand-primary"
                          : isDefault
                            ? "text-primary"
                            : "text-primary-on-dark",
                      )}
                    >
                      {category.label}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end">
                    <div className="hover:bg-button-ghost-hover relative flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-[100px]">
                      <TriangleIcon
                        direction={isExpanded ? "up" : "down"}
                        isDark={!isDefault}
                        isBrand={isActive}
                      />
                    </div>
                  </div>
                </button>

                {isExpanded && (
                  <div
                    className={clsx(
                      "flex w-full flex-col items-start gap-2 px-0 py-0",
                      "bg-background-secondary",
                    )}
                  >
                    <div className="flex min-h-10 w-full items-center justify-start gap-[10px] px-3 py-0">
                      <p className="text-label-2 text-tertiary shrink-0">
                        Sections
                      </p>
                    </div>
                    <div className="flex w-full flex-wrap items-stretch">
                      {[
                        {
                          label: category.label,
                          href: `${category.href}?ref=mobile-menu`,
                        },
                        ...categoryDropdownItems[category.label],
                      ].map((subCategory) => (
                        <Link
                          key={subCategory.href}
                          to={subCategory.href}
                          className="box-border flex w-1/2 min-w-0 items-center px-3 py-2"
                          onClick={onClose}
                          onClickCapture={() => {
                            sendDataToGTM({
                              event: "tcCustomEvent",
                              eventCategory: "mobile navigation",
                              eventAction: "click",
                              eventLabel: subCategory.label.toLowerCase(),
                            });
                          }}
                        >
                          <p
                            className={clsx(
                              "text-button-1 break-word w-full min-w-0 whitespace-normal",
                              isDefault
                                ? "text-secondary"
                                : "text-secondary-on-dark",
                            )}
                          >
                            {subCategory.label}
                          </p>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Main Navigation Links */}
        <div
          className={clsx(
            "border-divider-secondary flex flex-col items-start gap-2 px-0 py-4",
          )}
        >
          {mainNavLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className="flex h-8 w-full items-center px-3 py-0"
              onClick={() => {
                if (link.label.toLowerCase() === "newsletter") {
                  sendDataToGTM({
                    event: "tcCustomEvent",
                    eventCategory: "newsletter",
                    eventAction: "click",
                    eventLabel: pageUrl,
                  });
                } else {
                  sendDataToGTM({
                    event: "tcCustomEvent",
                    eventCategory: "mobile navigation",
                    eventAction: "click",
                    eventLabel: link.label.toLowerCase(),
                  });
                }
                onClose();
              }}
            >
              <p
                className={clsx(
                  "text-button-1 shrink-0",
                  isDefault ? "text-primary" : "text-primary-on-dark",
                )}
              >
                {link.label}
              </p>
            </Link>
          ))}
        </div>

        {/* Social Icons Footer */}
        <div
          className={clsx(
            "border-divider-brand mt-auto flex items-center justify-center gap-4 border-b-2 px-3 py-4",
            isDefault && "bg-background-secondary",
            theme === ThemeEnum.dark && "bg-background-secondary-on-dark",
          )}
        >
          <SocialIcons />
        </div>
      </div>
    </div>
  );
}
